import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Printer } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/Shared";

export default function CauseList() {
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [list, setList] = useState(null);
  useEffect(() => {
    setList(null);
    api.get("/cause-list", { params: { date } }).then(({ data }) => setList(data)).catch((e) => toast.error(errMsg(e)));
  }, [date]);

  return (
    <div data-testid="cause-list-page">
      <PageHeader eyebrow="Board" title="Cause List" description="Sittings listed before the tribunal on the chosen day, generated from the posted hearings." testId="cause-list-header"
        actions={
          <div className="flex items-center gap-3">
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="cause-list-date"
              className="h-10 px-3 border border-border bg-background text-sm rounded-none" />
            <button onClick={() => window.print()} data-testid="cause-list-print"
              className="inline-flex items-center h-10 px-4 bg-blue-900 hover:bg-blue-800 text-white uppercase tracking-widest text-xs transition-colors duration-200">
              <Printer className="h-3.5 w-3.5 mr-2" /> Print
            </button>
          </div>
        } />
      {!list ? <div className="h-40 border border-border bg-muted animate-pulse" /> : (
        <section className="border border-border bg-card print:border-0" data-testid="cause-list-sheet">
          <div className="p-6 border-b border-border text-center">
            <p className="font-display text-2xl">Cause List</p>
            <p className="label-caps mt-1" data-testid="cause-list-label">{list.label}</p>
          </div>
          {list.items.length === 0 ? <EmptyState testId="cause-list-empty" title="No sittings listed" hint="Nothing is posted for this day." /> : (
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border bg-muted/60">
                {["Item", "Time", "File no.", "Cause title", "Tribunal", "Stage / purpose", "Mode"].map((h) => <th key={h} className="p-3 text-left label-caps font-normal">{h}</th>)}
              </tr></thead>
              <tbody>
                {list.items.map((it) => (
                  <tr key={it.item} className="border-b border-border last:border-0 align-top" data-testid={`cause-item-${it.item}`}>
                    <td className="p-3 font-mono">{it.item}</td>
                    <td className="p-3 font-mono whitespace-nowrap">{it.time}</td>
                    <td className="p-3 font-mono text-xs"><Link to={`/cases/${it.case_id}`} className="text-blue-900 dark:text-blue-400 hover:underline">{it.case_number || it.ref_id}</Link></td>
                    <td className="p-3"><p className="font-medium">{it.claimant} v. {it.respondent}</p><p className="text-xs text-muted-foreground">{it.title}</p></td>
                    <td className="p-3 text-xs">{it.tribunal.join(", ")}</td>
                    <td className="p-3"><p className="text-xs uppercase tracking-wider">{it.hearing_type}</p><p className="text-xs text-muted-foreground">{it.purpose}</p></td>
                    <td className="p-3 text-xs capitalize">{it.mode}{it.venue ? ` · ${it.venue}` : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}
    </div>
  );
}
